import { CaseInsensitiveMap } from '../../data-structures/CaseInsensitiveMap';
import { decodeWords } from '../../mime-enc/decode-words';
import { parseHeaderValueAndParams } from '../../mime-headers/parseHeaderValueAndParams';
import type { ParsedHeaderValue } from '../../mime-headers/parseHeaderValueAndParams.types';
import { analyzeUtf8 } from '../../str-buf/analyzeUtf8';
import { UTF8_STATUS } from '../../str-buf/getHistoAndBasicAggs.types';
import { decodeStringNative } from '../../str-enc/native';
import type { StringDecoder } from '../../types';
import { doKeyPush } from '../../utils/map';
import type { IMimeHeadersParser } from './types';

interface IHeaderEntry {
	nameStart: number;
	nameEnd: number;
	valueStart: number;
	valueEnd: number;
}

const rxHeaderName = /[!-9;-~]+:/y;

export class MimeHeadersParser implements IMimeHeadersParser {
	public endPos = -1;
	public endStatus:
		| 'unknown'
		| 'empty'
		| 'double-nl'
		| 'single-nl-eof'
		| 'eof'
		| 'single-nl-start' = 'unknown';
	private _allHeaders: IHeaderEntry[] = [];
	private _byName = new CaseInsensitiveMap<string, IHeaderEntry[]>();
	private _areAllFoldingWsWsTab: boolean | null = null;
	public crlfCount = 0;
	public crCount = 0;
	public lfCount = 0;

	constructor(
		private _decode: StringDecoder,
		strictNameMode: boolean, // remove?
		private headersBinStr: string, // Should be binary string, 0x00-0xFF only

		start = 0,
		end = headersBinStr.length
	) {
		if (!Number.isSafeInteger(start) || start < 0 || start > this.headersBinStr.length) {
			throw new Error(`Expected start to be int in range [0, ${this.headersBinStr.length}]`);
		}

		if (!Number.isSafeInteger(end) || end < start || end > this.headersBinStr.length) {
			throw new Error(
				`Expected end to be int in range [${start}, ${this.headersBinStr.length}]`
			);
		}

		if (strictNameMode) {
			this._parseHeaders_indexOf(start, end, true);
		} else {
			this._parseHeaders_indexOf(start, end, false);
		}
	}

	private _rxNewline = /[\r\n]/g;

	private _findNewline(from: number): number {
		const rx = this._rxNewline;
		rx.lastIndex = from;
		return rx.exec(this.headersBinStr) ? rx.lastIndex - 1 : -1;
	}

	private _countAndSkipNewline(pos: number): number {
		const s = this.headersBinStr;
		if (s[pos] === '\r') {
			if (pos + 1 < s.length && s[pos + 1] === '\n') {
				this.crlfCount++;
				return pos + 2;
			}
			this.crCount++;
			return pos + 1;
		}
		this.lfCount++;
		return pos + 1;
	}

	private _parseHeaders_indexOf(start: number, end: number, strictName: boolean) {
		const s = this.headersBinStr;
		let pos = start;
		let currentHeader: IHeaderEntry | undefined;

		while (pos < end) {
			const ch = s[pos];

			if (ch === '\r' || ch === '\n') {
				this.endPos = this._countAndSkipNewline(pos);

				if (this._allHeaders.length > 0) {
					this.endStatus = 'double-nl';
				} else {
					this.endStatus = 'single-nl-start';
				}

				return;
			}

			if (ch === ' ' || ch === '\t') {
				if (!currentHeader) {
					throw new Error('Continuation without header');
				}
				const nlIdx = this._findNewline(pos);
				if (nlIdx < 0) {
					// Use whole rest of input
					currentHeader.valueEnd = end;
					this.endPos = end;
					return;
				}

				// End the value before the newline
				currentHeader.valueEnd = nlIdx;
				pos = this._countAndSkipNewline(nlIdx);
				continue;
			}

			const nameStart = pos;

			if (strictName) {
				rxHeaderName.lastIndex = pos;
				const nameMatch = rxHeaderName.exec(s);
				if (!nameMatch) {
					throw new Error('Truncated header line');
				}

				pos = nameMatch.index + nameMatch[0].length;
			} else {
				let colonIdx = -1;
				for (let i = pos; i < end; ++i) {
					if (s[i] === '\n' || s[i] === '\r') {
						throw new Error('Truncated header line');
					} else if (s[i] === ':') {
						colonIdx = i;
						break;
					}
				}

				if (colonIdx < 0) {
					throw new Error('Header line without colon');
				}

				pos = colonIdx + 1;
			}

			// At this point, pos is char after ":", end the name at the ":"
			const nameEndPlus1 = pos - 1;

			// We need the name sliced for the map
			const name = s.slice(nameStart, nameEndPlus1);

			if (s[pos] === ' ') {
				++pos;
			}

			// Allow to be -1 and represent whole line
			const nlIdx = this._findNewline(pos);

			currentHeader = {
				// name,
				nameStart,
				nameEnd: nameEndPlus1,
				valueStart: pos,
				valueEnd: nlIdx < 0 || nlIdx > end ? end : nlIdx,
			};

			this._allHeaders.push(currentHeader);

			doKeyPush(this._byName, name, currentHeader);
			if (nlIdx < 0 || nlIdx > end) {
				this.endPos = pos = end;
				this.endStatus = 'eof';
				return;
			}

			pos = this._countAndSkipNewline(nlIdx);
		}

		// If we get here, we read a header and newline
		this.endPos = pos;

		if (end === start) {
			this.endStatus = 'empty';
		} else if (this._allHeaders.length > 0) {
			this.endStatus = 'single-nl-eof';
		}
	}

	public get numHeaders(): number {
		return this._allHeaders.length;
	}

	public getAllHeadersRaw() {
		const entries = this._allHeaders;
		return entries.map(e => {
			const name = this.headersBinStr.slice(e.nameStart, e.nameEnd);
			let raw = this.headersBinStr.slice(e.valueStart, e.valueEnd);
			// unfold: replace CRLF/CR/LF + WSP with single space
			raw = raw.replace(/(?:\r\n|\r|\n)[ \t]/g, ' ');
			return {
				name,
				value: raw,
			};
		});
	}

	public hasHeader(name: string) {
		return this._byName.has(name);
	}

	/**
	 * Get all raw values for a header (unfolded, trimmed)
	 */
	public getHeadersRaw(
		name: string,
		mode: 'RAWRAW' | 'POST-TRIM' | 'OUTLOOK-TRIM' = 'POST-TRIM'
	): string[] | undefined {
		const entries = this._byName.get(name);

		// In Outlook mode, we trim the start before whitespace unfolding and not after
		if (mode === 'RAWRAW') {
			return entries?.map(e => {
				return this.headersBinStr.slice(e.valueStart, e.valueEnd);
			});
		} else if (mode === 'OUTLOOK-TRIM') {
			return entries?.map(e => {
				let raw = this.headersBinStr.slice(e.valueStart, e.valueEnd);
				if (raw.startsWith(' \r\n ')) {
					// Leave the space
					raw = raw.slice(3);
				} else if (raw.startsWith(' \r\n\t')) {
					// Remove all 4
					raw = raw.slice(4);
				} else if (raw.startsWith(' \r ')) {
					raw = raw.slice(2);
				} else if (raw.startsWith(' \r\t')) {
					raw = raw.slice(3);
				} else if (raw.startsWith('\r\n ') || raw.startsWith('\r\n\t')) {
					raw = raw.slice(3);
				} else if (raw.startsWith('\r ') || raw.startsWith('\r\t')) {
					raw = raw.slice(2);
				} else if (raw.startsWith(' ')) {
					raw = raw.slice(1);
				}

				if (raw.endsWith('\r\n')) {
					raw = raw.slice(0, -2);
				} else if (raw.endsWith('\n') || raw.endsWith('\r')) {
					raw = raw.slice(0, -1);
				}

				// unfold: replace CRLF/CR/LF + WSP with space, but not tab
				raw = raw.replace(/(?:\r\n|\r|\n)([ \t])/g, (whole, match) => {
					return match === ' ' ? match : '';
				});

				return raw;
			});
		} else {
			return entries?.map(e => {
				return this.headersBinStr
					.slice(e.valueStart, e.valueEnd)
					.replace(/(?:\r\n|\r|\n)[ \t]/g, ' ');
			});
		}
	}

	/**
	 * In some rare instances, a raw header may be UTF-7 encoded, which can be
	 * detected based on some other header or MIME values. Out of caution for
	 * now, we only want to do this when explicitly wanted and the value
	 * starts or ends with a known UTF-7 character.
	 * @param name
	 */
	public getHeadersRawUnlessUtf7Bracketed(name: string) {
		return this.getHeadersRaw(name)?.map(s => {
			if (s.startsWith('+ADw-') || s.endsWith('+AD4-')) {
				return this._decode(Buffer.from(s, 'binary'), 'utf7');
			} else {
				return s;
			}
		});
	}

	public decodeHeaderValue(str: string): string {
		// Should be binary string already
		// if (/[^\x00-\xFF]/.test(str)) {
		// 	// Not a binary string
		// 	throw new Error('Expected binary string for header decoding');
		/* } else */
		if (/[\x80-\xFF]/.test(str)) {
			// We got 8-bit chars, check UTF-8?
			const utf8Status = analyzeUtf8(str);
			if (
				utf8Status.numInvalidUtf8Seqs === 0 &&
				utf8Status.numValidUtf8Seqs > 0 &&
				utf8Status.utf8State === UTF8_STATUS.ACCEPT
			) {
				// Recode to UTF-8
				str = Buffer.from(str, 'binary').toString('utf8');
			} else {
				// CP1252?
				// TODO: Maybe try to detect other encodings?
				// TODO: Just recode the high-byte values for perf?
				str = decodeStringNative(Buffer.from(str, 'binary'), 'cp1252');
			}
		}

		// 7-bit (as expected), but may have MIME encoded words
		return decodeWords(str, this._decode);
	}

	public getHeadersDecoded(
		name: string,
		mode: 'POST-TRIM' | 'OUTLOOK-TRIM' = 'POST-TRIM'
	): string[] | undefined {
		const raw = this.getHeadersRaw(name, mode);
		const decoded = raw?.map(str => this.decodeHeaderValue(str));
		return decoded;
	}

	public getHeadersDecodedWParams(name: string): ParsedHeaderValue[] | undefined {
		return this.getHeadersRaw(name)?.map(str => {
			if (/[\x80-\xFF]/.test(str)) {
				throw new Error(`Header ${name} has 8-bit characters, cannot decode directly`);
			}
			return parseHeaderValueAndParams(str, this._decode);
		});
	}

	/**
	 * Get first raw header value
	 */
	public getFirstHeaderRaw(name: string): string | undefined {
		const arr = this.getHeadersRaw(name);
		return arr?.[0];
	}

	public getSingleAndDecode(name: string): string | undefined {
		const rawVals = this.getHeadersRaw(name);
		if (rawVals && rawVals.length) {
			if (rawVals.length === 1) {
				return this.decodeHeaderValue(rawVals[0]);
			} else {
				throw new Error(`Got multiple headers with name ${name}`);
			}
		}
	}

	/**
	 * Does not handle raw 8-bit values
	 * @param name
	 * @returns
	 */
	public getSingleAndDecodeWParams(name: string): ParsedHeaderValue | undefined {
		const rawVals = this.getHeadersRaw(name);
		if (rawVals && rawVals.length) {
			if (rawVals.length === 1) {
				if (/[\x80-\xFF]/.test(rawVals[0])) {
					throw new Error(`Header ${name} has 8-bit characters, cannot decode directly`);
				}
				return parseHeaderValueAndParams(rawVals[0], this._decode);
			} else {
				throw new Error(`Got multiple headers with name ${name}`);
			}
		}
	}
}
